// Wrapper que decide se uma rota de "modal" (transactions/new, expenses/new,
// incomes/new) deve renderizar:
//  - como pagina cheia (deep-link direto, F5, share de URL),
//  - ou como Sheet por cima da rota anterior (navegacao via FAB / "Novo").
//
// Estrategia: ao navegar via React Router, passamos `state.backgroundLocation`
// apontando para a rota anterior. Este componente checa esse state — se
// presente, monta o conteudo dentro de `<Sheet/>`; se ausente, renderiza
// como pagina full.
//
// Diferenca do padrao "modal-routes" do React Router docs: como nosso shell
// ja tem layout proprio (Outlet do AppLayout), o Sheet abre por cima desse
// shell sem reorganizar a arvore de rotas.

import type { ComponentType, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Sheet } from '@/shared/components/ui/sheet';

export interface ModalRouteWrapperProps {
  title: string;
  testID?: string;
  children: ReactNode;
  /**
   * Repassa para o `<Sheet/>`: no mobile a bandeja abre com altura fixa
   * (90dvh) em vez de dirigida pelo conteudo. Usado em rotas cujo conteudo
   * pode ser curto e ficava com a bandeja pequena demais (ex: categorias).
   */
  mobileFullHeight?: boolean;
}

interface ModalLocationState {
  backgroundLocation?: { pathname: string; search: string };
}

/**
 * Envelope que decide entre pagina full e Sheet. `children` e o conteudo
 * da rota (ex: <CreateTransactionScreen/>).
 *
 * Uso direto em rotas:
 *   {
 *     path: 'transactions/new',
 *     element: (
 *       <ModalRouteWrapper title="Nova transacao">
 *         <CreateTransactionScreen />
 *       </ModalRouteWrapper>
 *     ),
 *   }
 */
export function ModalRouteWrapper({
  title,
  testID,
  children,
  mobileFullHeight = false,
}: ModalRouteWrapperProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as ModalLocationState | null;
  const isModal = !!state?.backgroundLocation;

  if (!isModal) {
    return (
      <div data-testid={testID} className="mx-auto w-full max-w-xl space-y-4">
        <h1 className="text-display-md text-on-surface">{title}</h1>
        {children}
      </div>
    );
  }

  return (
    <Sheet
      open
      onOpenChange={(next) => {
        if (!next) navigate(-1);
      }}
      title={title}
      mobileFullHeight={mobileFullHeight}
      {...(testID ? { testID } : {})}
    >
      {/* full-height: no mobile o conteudo preenche o drawer (flex-1) e no
          desktop volta ao cap por max-h (md = 900px, ponto de troca do Sheet). */}
      <div
        className={
          mobileFullHeight
            ? 'min-h-0 flex-1 overflow-y-auto pr-1 md:max-h-[80dvh]'
            : 'max-h-[80dvh] overflow-y-auto pr-1'
        }
      >
        {children}
      </div>
    </Sheet>
  );
}

/**
 * HOC para rotas que precisam ser modal-aware. Retorna um componente que
 * envelopa o screen com `<ModalRouteWrapper/>` e o titulo apropriado.
 */
export function withModalRoute<P extends object>(
  Component: ComponentType<P>,
  options: { title: string; testID?: string },
) {
  function ModalRoute(props: P) {
    return (
      <ModalRouteWrapper
        {...(options.testID ? { testID: options.testID } : {})}
        title={options.title}
      >
        <Component {...props} />
      </ModalRouteWrapper>
    );
  }
  ModalRoute.displayName = `withModalRoute(${Component.displayName ?? Component.name ?? 'Component'})`;
  return ModalRoute;
}
